import Head from 'next/head';
import Image from 'next/image';
import getConfig from 'next/config';
import { ImgHTMLAttributes } from 'react';

const { NEXT_PUBLIC_API_URL = process.env.API_URL } = getConfig().publicRuntimeConfig;

export type ImageSize = {
  width: number;
  // NOTE: either height or aspectRatio must be provided
  height?: number;
  aspectRatio?: number;
};

export type ResponsiveImageT = {
  src: string;
  size: ImageSize;
  breakpoint: number;
  dpis?: number[];
  quality?: number;
};

export type ResponsiveImageSet = ResponsiveImageT[];

export type ResponsiveImageLoading = 'eager' | 'lazy' | undefined;
export type FetchPriority = 'low' | 'high' | undefined;

export type ResponsiveImageProps = {
  sources?: ResponsiveImageSet;
  alt?: string;
  className?: string;
  style?: any;
  loading?: ResponsiveImageLoading;
  fetchpriority?: FetchPriority;
  preload?: boolean;
};

const domain = (NEXT_PUBLIC_API_URL || 'https://sandbox3.brilliantearth.com/').replace(/\/$/, '');
/**
 * FIX: This should be under src/components/ folder
 */
export default function ResponsiveImage({
  sources,
  alt,
  className,
  style = {},
  loading = 'lazy',
  fetchpriority,
  preload = false,
}: ResponsiveImageProps) {
  const baseImage = {
    src: '',
    width: 0,
    height: 0,
  };

  const conditionalProps = {
    loading,
    fetchpriority,
  } as ImgHTMLAttributes<HTMLImageElement>;

  let previousBreakPoint = 0;
  let preloadSrcs: Array<string> = [];
  const sourceTags = Array.isArray(sources)
    ? sources
        .sort((a, b) => a.breakpoint - b.breakpoint)
        .map((source, i, self) => {
          const { size } = source;
          const { width } = size;
          const isLast = i + 1 === self.length;

          const height = Math.round(size.height || size.width / (size.aspectRatio || 1));
          const quality = source.quality || 100;
          const dpis = source.dpis || [1, 2];
          const sanitizedSource = (source.src ?? '').replace(/ /g, '%20');
          const url = (/^http/.test(source.src) ? '' : domain) + sanitizedSource;

          const urls = dpis.map(
            dpi =>
              `https://image.brilliantearth.com/cdn-cgi/image/width=${Math.round(width * dpi)},height=${Math.round(
                height * dpi
              )},quality=${quality},format=auto/${url} ${dpi}x`
          );

          if (preload) {
            // Why are 1 digit dpis from being removed from the end of URLs ?
            preloadSrcs = preloadSrcs.concat(urls.map(str => str.replace(/\s\d{1}x$/, '')));
          }

          const media = isLast
            ? `(min-width: ${previousBreakPoint}px)`
            : `(min-width: ${previousBreakPoint}px) and (max-width: ${source.breakpoint}px)`;

          if (i === 0) {
            // Removes dpi from the end of the URL
            baseImage.src = urls[0].replace(/ +[0-9\.]+x$/i, '');
            baseImage.width = width;
            baseImage.height = height;
          }

          previousBreakPoint = source.breakpoint + 1;

          return <source srcSet={urls.join(', ')} media={media} key={i} />;
        })
    : null;

  const srcSet = new Set(preloadSrcs);

  const linksToPreload = [...srcSet].map(src => <link key={src} rel="preload" href={src} as="image" />);

  return (
    <>
      {preload && <Head>{linksToPreload}</Head>}

      <picture>
        {sourceTags}
        <Image
          {...conditionalProps}
          src={baseImage.src}
          alt={alt || ''}
          className={className}
          width={baseImage.width}
          height={baseImage.height}
          style={style}
        />
      </picture>
    </>
  );
}
